'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import { useTranslations } from 'next-intl';

import { useFilters } from '@/src/lib/filters';
import { useCurrentUser, useDeleteSavedSearch, useSavedSearches } from '@/src/lib/hooks/query';
import {
  buildSavedSearchMatchKey,
  findMatchingSavedSearch,
  getSavedSearchId,
  type SavedSearchAreaBounds,
} from '@/src/lib/savedSearches';
import { SaveSearchModal } from '@/src/components/ListingSearch/SaveSearchModal';
import { ListingActionSignInModal } from '@/src/components/features/listing-actions/ListingActionSignInModal';
import { emitTrackCreateSearchAlertEvent, emitTrackOpenSearchAlertModalEvent } from '@/src/analytics/hotpads/events';

const PERSISTED_SAVED_SEARCH_KEY = 'hp:saved-search-match-key';
const SAVE_SEARCH_REDIRECT_PARAM = 'saveSearch';

function getCurrentRelativeUrl(): string {
  if (typeof window === 'undefined') {
    return '/';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function withSaveSearchRedirectParam(path: string): string {
  const url = new URL(path, 'https://hotpads.com');
  url.searchParams.set(SAVE_SEARCH_REDIRECT_PARAM, '1');
  return `${url.pathname}${url.search}${url.hash}`;
}

function readSaveSearchResumeFromUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const url = new URL(window.location.href);
  if (url.searchParams.get(SAVE_SEARCH_REDIRECT_PARAM) !== '1') {
    return false;
  }

  url.searchParams.delete(SAVE_SEARCH_REDIRECT_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return true;
}

function readPersistedSavedSearchKey(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(PERSISTED_SAVED_SEARCH_KEY);
}

function persistSavedSearchKey(key: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(PERSISTED_SAVED_SEARCH_KEY, key);
}

function clearPersistedSavedSearchKey(key?: string) {
  if (typeof window === 'undefined') {
    return;
  }

  const currentKey = window.sessionStorage.getItem(PERSISTED_SAVED_SEARCH_KEY);
  if (key && currentKey !== key) {
    return;
  }

  window.sessionStorage.removeItem(PERSISTED_SAVED_SEARCH_KEY);
}

type SrpSaveSearchContextValue = {
  isSaved: boolean;
  isDisabled: boolean;
  label: string;
  handleToggle: () => void;
};

type SrpSaveSearchProviderProps = {
  children: ReactNode;
  resourceId: string;
  areaId: string;
  searchSlug: string;
  locationText: string;
  areaBounds: SavedSearchAreaBounds;
};

const SrpSaveSearchContext = createContext<SrpSaveSearchContextValue | null>(null);

export function SrpSaveSearchProvider({
  children,
  resourceId,
  areaId,
  searchSlug,
  locationText,
  areaBounds,
}: SrpSaveSearchProviderProps) {
  const t = useTranslations('search');
  const tAuth = useTranslations('auth');
  const { filters } = useFilters();
  const { data: currentUser } = useCurrentUser();
  const hasResolvedCurrentUser = currentUser !== undefined;
  const isLoggedIn = currentUser?.loggedIn ?? false;
  const savedSearchesQuery = useSavedSearches({ enabled: isLoggedIn });
  const deleteSavedSearch = useDeleteSavedSearch();
  const savedSearchMatchKey = useMemo(
    () =>
      buildSavedSearchMatchKey({
        resourceId,
        areaId,
        searchSlug,
        filters,
      }),
    [areaId, filters, resourceId, searchSlug],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authRedirectPath, setAuthRedirectPath] = useState('/');
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [optimisticSaved, setOptimisticSaved] = useState(false);
  const [pendingRemovalSearchId, setPendingRemovalSearchId] = useState<string | null>(null);

  const matchingSearch = useMemo(
    () =>
      findMatchingSavedSearch(savedSearchesQuery.data, {
        resourceId,
        areaId,
        searchSlug,
        filters,
      }),
    [areaId, filters, resourceId, savedSearchesQuery.data, searchSlug],
  );

  const matchingSearchId = matchingSearch ? getSavedSearchId(matchingSearch) : null;
  const isPendingRemoval = !!matchingSearchId && matchingSearchId === pendingRemovalSearchId;

  const openSaveSearchModal = useCallback(() => {
    emitTrackOpenSearchAlertModalEvent();
    setIsModalOpen(true);
  }, []);

  const openAuthModal = useCallback(() => {
    setAuthRedirectPath(withSaveSearchRedirectParam(getCurrentRelativeUrl()));
    setIsAuthModalOpen(true);
  }, []);

  useEffect(() => {
    if (pendingRemovalSearchId && pendingRemovalSearchId !== matchingSearchId) {
      setPendingRemovalSearchId(null);
    }
  }, [matchingSearchId, pendingRemovalSearchId]);

  useEffect(() => {
    const persistedKey = readPersistedSavedSearchKey();
    if (persistedKey === savedSearchMatchKey) {
      setOptimisticSaved(true);
      return;
    }

    if (!matchingSearch && hasResolvedCurrentUser) {
      setOptimisticSaved(false);
    }
  }, [hasResolvedCurrentUser, matchingSearch, savedSearchMatchKey]);

  useEffect(() => {
    if (matchingSearch) {
      persistSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(true);
      return;
    }

    if (hasResolvedCurrentUser && !isLoggedIn) {
      clearPersistedSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(false);
      return;
    }

    if (isLoggedIn && !savedSearchesQuery.isPending && !savedSearchesQuery.isFetching) {
      clearPersistedSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(false);
    }
  }, [
    hasResolvedCurrentUser,
    isLoggedIn,
    matchingSearch,
    savedSearchMatchKey,
    savedSearchesQuery.isFetching,
    savedSearchesQuery.isPending,
  ]);

  // Read (and strip) the resume param only once identity resolves, so a reload mid-resolution keeps the intent.
  useEffect(() => {
    if (!hasResolvedCurrentUser || !readSaveSearchResumeFromUrl()) {
      return;
    }

    if (isLoggedIn) {
      setPendingModalOpen(true);
      return;
    }

    openAuthModal();
  }, [hasResolvedCurrentUser, isLoggedIn, openAuthModal]);

  // Wait for the saved-search list to settle so an already-saved search doesn't reopen the create modal.
  useEffect(() => {
    if (!pendingModalOpen || !isLoggedIn) {
      return;
    }

    if (savedSearchesQuery.isPending || savedSearchesQuery.isFetching) {
      return;
    }

    setPendingModalOpen(false);
    if (!matchingSearch) {
      openSaveSearchModal();
    }
  }, [
    isLoggedIn,
    matchingSearch,
    openSaveSearchModal,
    pendingModalOpen,
    savedSearchesQuery.isFetching,
    savedSearchesQuery.isPending,
  ]);

  const isSaved = (!isPendingRemoval && !!matchingSearch) || optimisticSaved;
  const isWaitingForSavedState =
    (!hasResolvedCurrentUser && optimisticSaved) ||
    (isLoggedIn && savedSearchesQuery.isPending && savedSearchesQuery.data === undefined) ||
    (isLoggedIn && optimisticSaved && !matchingSearch);
  const isDisabled = !hasResolvedCurrentUser || deleteSavedSearch.isPending || isWaitingForSavedState;

  const handleToggle = useCallback(async () => {
    await interactionResponse();

    if (isDisabled) {
      return;
    }

    const searchId = matchingSearchId;
    if (searchId) {
      setPendingRemovalSearchId(searchId);
      deleteSavedSearch.mutate(searchId, {
        onSuccess: () => {
          clearPersistedSavedSearchKey(savedSearchMatchKey);
          setOptimisticSaved(false);
        },
        onError: () => {
          setPendingRemovalSearchId(null);
        },
      });
      return;
    }

    if (!isLoggedIn) {
      openAuthModal();
      return;
    }

    openSaveSearchModal();
  }, [
    deleteSavedSearch,
    isDisabled,
    isLoggedIn,
    matchingSearchId,
    openAuthModal,
    openSaveSearchModal,
    savedSearchMatchKey,
  ]);

  const value = useMemo<SrpSaveSearchContextValue>(
    () => ({
      isSaved,
      isDisabled,
      label: isSaved ? t('savedSearch') : t('saveSearch'),
      handleToggle,
    }),
    [handleToggle, isDisabled, isSaved, t],
  );

  return (
    <SrpSaveSearchContext.Provider value={value}>
      {children}
      <SaveSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={() => {
          emitTrackCreateSearchAlertEvent({
            triggerLocation: 'search_results',
            triggerObject: 'search_results|alerts_modal',
          });
          persistSavedSearchKey(savedSearchMatchKey);
          setOptimisticSaved(true);
        }}
        areaId={areaId}
        resourceId={resourceId}
        searchSlug={searchSlug}
        locationText={locationText}
        areaBounds={areaBounds}
      />
      <ListingActionSignInModal
        open={isAuthModalOpen}
        onOpenChange={setIsAuthModalOpen}
        onAuthenticated={() => {
          setIsAuthModalOpen(false);
          setPendingModalOpen(true);
        }}
        postLoginRedirect={authRedirectPath}
        title={tAuth('saveSearchPromptTitle')}
        intro={tAuth('saveSearchPromptIntro')}
        testId="save-search-sign-in-modal"
      />
    </SrpSaveSearchContext.Provider>
  );
}

export function useSrpSaveSearch(): SrpSaveSearchContextValue {
  const context = useContext(SrpSaveSearchContext);

  if (!context) {
    return {
      isSaved: false,
      isDisabled: true,
      label: 'Save search',
      handleToggle: () => {},
    };
  }

  return context;
}
